import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, CardContent, CardHeader, CardTitle, Input } from '@lms/ui';
import { useAuth } from '@/hooks/useAuth';
import { ApiRequestError, api } from '@/lib/api';
import { LegalPolicyConsentField } from '@/features/legal-policies/LegalPolicyConsentField';
import type { LegalPolicyConsentItem } from '@/features/legal-policies/types';

interface RegisterForm {
  name: string;
  email: string;
  password: string;
}

export function RegisterPage() {
  const { register: registerUser, isRegistering, registerError } = useAuth();
  const { register, handleSubmit } = useForm<RegisterForm>();
  const [acceptedLegalPolicyIds, setAcceptedLegalPolicyIds] = useState<Set<string>>(() => new Set());

  const { data: policiesData, isLoading: policiesLoading } = useQuery({
    queryKey: ['legal-policies', 'active', 'register'],
    queryFn: () => api<{ data: LegalPolicyConsentItem[] }>('/legal/policies/active'),
    select: (response) => response.data.filter((policy) => policy.requiredAtRegister),
  });

  const registerPolicies = policiesData ?? [];

  const allPoliciesAccepted = useMemo(() => {
    if (registerPolicies.length === 0) return true;
    return registerPolicies.every((policy) => acceptedLegalPolicyIds.has(policy.id));
  }, [registerPolicies, acceptedLegalPolicyIds]);

  const onSubmit = handleSubmit(async (values) => {
    await registerUser({
      ...values,
      acceptedLegalPolicyIds: registerPolicies.map((policy) => policy.id).filter((id) => acceptedLegalPolicyIds.has(id)),
    });
  });

  const message =
    registerError instanceof ApiRequestError ? registerError.message : registerError?.message;

  function handlePolicyToggle(policyId: string, accepted: boolean) {
    setAcceptedLegalPolicyIds((prev) => {
      const next = new Set(prev);
      if (accepted) next.add(policyId);
      else next.delete(policyId);
      return next;
    });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Crear cuenta</CardTitle>
        </CardHeader>
        <CardContent>
          {message && (
            <Alert variant="danger" className="mb-4">
              {message}
            </Alert>
          )}
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <Input label="Nombre" autoComplete="name" {...register('name', { required: true })} />
            <Input label="Correo electrónico" type="email" autoComplete="email" {...register('email', { required: true })} />
            <Input label="Contraseña" type="password" autoComplete="new-password" {...register('password', { required: true, minLength: 8 })} />

            {policiesLoading ? (
              <p className="text-sm text-on-surface-variant">Cargando políticas legales…</p>
            ) : registerPolicies.length > 0 ? (
              <LegalPolicyConsentField
                policies={registerPolicies}
                acceptedIds={acceptedLegalPolicyIds}
                onToggle={handlePolicyToggle}
                disabled={isRegistering}
              />
            ) : null}

            <Button type="submit" loading={isRegistering} className="w-full" disabled={!allPoliciesAccepted}>
              Registrarme
            </Button>
          </form>
          <p className="mt-4 text-center text-sm text-on-surface-variant">
            ¿Ya tienes cuenta?{' '}
            <Link to="/login" className="font-medium text-primary hover:underline">
              Inicia sesión
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
